import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { 
  Clock, MapPin, LogIn, LogOut, Loader2, CheckCircle2, 
  AlertCircle, Navigation, Timer
} from 'lucide-react';

const CENTRE_LOCATION = {
  latitude: 51.5478,
  longitude: 0.0365,
  radius: 50 // meters
};

const ClockInOut = () => {
  const { api, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showClockOutDialog, setShowClockOutDialog] = useState(false);
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [distance, setDistance] = useState(null);

  // Calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const phi1 = (lat1 * Math.PI) / 180;
    const phi2 = (lat2 * Math.PI) / 180;
    const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

    const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
              Math.cos(phi1) * Math.cos(phi2) *
              Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Fetch current status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/attendance/status');
      setStatus(res.data);
    } catch (err) {
      console.error('Error fetching attendance status:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStatus();
    // Refresh status every minute
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Get current location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      setGettingLocation(true);
      setLocationError(null);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setLocation(loc);
          
          // Calculate distance from centre
          const dist = calculateDistance(
            loc.latitude, loc.longitude,
            CENTRE_LOCATION.latitude, CENTRE_LOCATION.longitude
          );
          setDistance(Math.round(dist));
          setGettingLocation(false);
          resolve(loc);
        },
        (error) => {
          let errorMsg = 'Unable to get your location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = 'Location permission denied. Please enable location access.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = 'Location information unavailable.';
              break;
            case error.TIMEOUT:
              errorMsg = 'Location request timed out.';
              break;
          }
          setLocationError(errorMsg);
          setGettingLocation(false);
          reject(new Error(errorMsg));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  // Handle Clock In
  const handleClockIn = async () => {
    try {
      setClockingIn(true);
      const loc = await getCurrentLocation();
      
      const res = await api.post('/attendance/clock-in', {
        latitude: loc.latitude,
        longitude: loc.longitude,
        accuracy: loc.accuracy
      });
      
      toast.success(res.data.message);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to clock in');
    } finally {
      setClockingIn(false);
    }
  };

  // Handle Clock Out
  const handleClockOut = async () => {
    try {
      setClockingOut(true);
      const loc = await getCurrentLocation();
      
      const res = await api.post('/attendance/clock-out', {
        latitude: loc.latitude,
        longitude: loc.longitude,
        accuracy: loc.accuracy,
        break_minutes: breakMinutes
      });
      
      toast.success(`Clocked out! Total hours: ${res.data.total_hours}h`);
      setShowClockOutDialog(false);
      setBreakMinutes(0);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to clock out');
    } finally {
      setClockingOut(false);
    }
  };

  // Format elapsed time
  const formatElapsedTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-4 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  const isWithinRange = distance !== null && distance <= CENTRE_LOCATION.radius;

  return (
    <>
      <Card className={`border-2 ${status?.is_clocked_in ? 'border-green-300 bg-green-50' : 'border-slate-200'}`}>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${status?.is_clocked_in ? 'bg-green-100' : 'bg-slate-100'}`}>
                <Clock className={`w-5 h-5 ${status?.is_clocked_in ? 'text-green-600' : 'text-slate-500'}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">Time Tracking</span>
                  {status?.is_clocked_in ? (
                    <Badge className="bg-green-500 text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Clocked In
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Not Clocked In</Badge>
                  )}
                </div>
                {status?.is_clocked_in && (
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-600">
                    <Timer className="w-3 h-3" />
                    <span>Working: {formatElapsedTime(status.elapsed_hours)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Location Status */}
              {distance !== null && (
                <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                  isWithinRange ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  <MapPin className="w-3 h-3" />
                  {distance}m {isWithinRange ? '✓' : 'away'}
                </div>
              )}

              {/* Get Location Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={getCurrentLocation}
                disabled={gettingLocation}
                className="text-xs"
              >
                {gettingLocation ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Navigation className="w-3 h-3" />
                )}
              </Button>

              {/* Clock In/Out Button */}
              {status?.is_clocked_in ? (
                <Button
                  onClick={() => setShowClockOutDialog(true)}
                  disabled={clockingOut}
                  variant="destructive"
                  size="sm"
                  className="text-xs"
                >
                  {clockingOut ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <LogOut className="w-3 h-3 mr-1" />
                  )}
                  Clock Out
                </Button>
              ) : (
                <Button
                  onClick={handleClockIn}
                  disabled={clockingIn}
                  className="bg-green-600 hover:bg-green-700 text-xs"
                  size="sm"
                >
                  {clockingIn ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <LogIn className="w-3 h-3 mr-1" />
                  )}
                  Clock In
                </Button>
              )}
            </div>
          </div>

          {locationError && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 flex items-center gap-2">
              <AlertCircle className="w-3 h-3" />
              {locationError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clock Out Dialog */}
      <Dialog open={showClockOutDialog} onOpenChange={setShowClockOutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clock Out</DialogTitle>
            <DialogDescription>
              Please confirm your clock out and enter any break time taken.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm text-slate-600">Working since:</p>
              <p className="font-medium">
                {status?.clock_in_time && new Date(status.clock_in_time).toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Elapsed: {formatElapsedTime(status?.elapsed_hours || 0)}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="break">Break time (minutes)</Label>
              <Input
                id="break"
                type="number"
                min="0"
                max="240"
                value={breakMinutes}
                onChange={(e) => setBreakMinutes(parseInt(e.target.value) || 0)}
                placeholder="0"
              />
              <p className="text-xs text-slate-500">
                Enter total break time taken (lunch, tea breaks, etc.)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClockOutDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleClockOut} 
              disabled={clockingOut}
              variant="destructive"
            >
              {clockingOut ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <LogOut className="w-4 h-4 mr-2" />
              )}
              Confirm Clock Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClockInOut;
